/**
 * Pure, testable description of "what is happening at the table right now"
 * derived from the websocket game state. Used by the turn banner (both guest
 * and account tables) and the results panel.
 */

export type Suit = 'spades' | 'hearts' | 'diamonds' | 'clubs';

export interface TurnCard {
  userID: string;
  suite: Suit;
  value: string;
}

export interface TurnPlayer {
  id: string;
  name: string;
  cardsLeft: number;
  userType?: string;
}

export interface TurnRoom {
  nextTurn: string | null;
  isFull: boolean;
  isConcluded?: boolean;
  numberOfPlayers: number;
  lastTurnResult: string | null;
}

export interface TurnInput {
  userID: string;
  room: TurnRoom | null | undefined;
  inPlayCards: TurnCard[] | null | undefined;
  userDeck: TurnCard[] | null | undefined;
  players: TurnPlayer[] | null | undefined;
}

export type TurnTone =
  | 'waiting'
  | 'your-turn'
  | 'their-turn'
  | 'pickup'
  | 'escaped'
  | 'over';

export interface TurnDescription {
  tone: TurnTone;
  title: string;
  detail: string;
  leadSuit: Suit | null;
  /** Whether the local player must follow the lead suit this turn. */
  mustFollow: boolean;
}

export const SUIT_GLYPH: Record<Suit, string> = {
  spades: '♠',
  hearts: '♥',
  diamonds: '♦',
  clubs: '♣',
};

export const SUIT_NAME: Record<Suit, string> = {
  spades: 'spades',
  hearts: 'hearts',
  diamonds: 'diamonds',
  clubs: 'clubs',
};

const RANK_NAME: Record<string, string> = {
  A: 'Ace',
  K: 'King',
  Q: 'Queen',
  J: 'Jack',
};

export const cardLabel = (card: { value: string; suite: Suit }) => {
  const v = card.value.trim();
  return `${RANK_NAME[v] || v} of ${SUIT_NAME[card.suite]}`;
};

export interface ParsedLastTurn {
  userLost: string | null;
  draw?: boolean;
  bazzi: boolean;
  thoka: boolean;
  userCallBazzi: string | null;
}

export const parseLastTurn = (
  raw: string | null | undefined
): ParsedLastTurn => {
  try {
    const parsed = JSON.parse(raw || '{}') || {};
    const turnRes = parsed.turnRes || {};
    return {
      userLost: turnRes.userLost ? String(turnRes.userLost) : null,
      draw: !!turnRes.draw,
      bazzi: !!turnRes.bazzi,
      thoka: !!turnRes.thoka,
      userCallBazzi: parsed.userCallBazzi ? String(parsed.userCallBazzi) : null,
    };
  } catch {
    return { userLost: null, bazzi: false, thoka: false, userCallBazzi: null };
  }
};

/** Find a player by id or (the API sometimes reports names) by name. */
export const findPlayer = (
  players: TurnPlayer[] | null | undefined,
  idOrName: string | null | undefined
) => {
  if (!idOrName || !players) return undefined;
  return (
    players.find(p => p.id === idOrName) ||
    players.find(p => p.name === idOrName)
  );
};

/**
 * The game is over when someone has been named the Bhabi and at most one
 * player is still holding cards. (A rematch re-deals, so cardsLeft goes back
 * up even though lastTurnResult may still carry the previous loser.)
 */
export const isGameOver = (input: TurnInput): boolean => {
  if (input.room?.isConcluded) return true;
  const { userLost } = parseLastTurn(input.room?.lastTurnResult);
  if (!userLost) return false;
  const holding = (input.players || []).filter(p => p.cardsLeft > 0);
  return holding.length <= 1;
};

const nameOf = (
  players: TurnInput['players'],
  id: string | null,
  me: string
) => {
  if (!id) return 'Someone';
  if (id === me) return 'You';
  return findPlayer(players, id)?.name || 'Someone';
};

export const describeTurn = (input: TurnInput): TurnDescription => {
  const { userID, room, players } = input;
  const inPlayCards = input.inPlayCards || [];
  const userDeck = input.userDeck || [];
  const leadSuit = (inPlayCards[0]?.suite as Suit | undefined) || null;
  const base = { leadSuit, mustFollow: false };

  if (!room) {
    return {
      ...base,
      tone: 'waiting',
      title: 'Finding your seat…',
      detail: '',
    };
  }

  if (!room.isFull) {
    const seated = players?.length || 0;
    return {
      ...base,
      tone: 'waiting',
      title: 'Waiting for players',
      detail: `${seated} of ${room.numberOfPlayers} seats filled. Cards are dealt the moment the last seat fills.`,
    };
  }

  const last = parseLastTurn(room.lastTurnResult);

  if (isGameOver(input)) {
    if (last.draw)
      return {
        ...base,
        tone: 'over',
        title: 'Everyone escaped!',
        detail: 'No Bhabi this time. Deal again with the same table.',
      };
    const loser = findPlayer(players, last.userLost);
    const loserIsMe = loser?.id === userID;
    return {
      ...base,
      tone: 'over',
      title: loserIsMe
        ? 'You are the Bhabi this time'
        : `${loser?.name || last.userLost} is the Bhabi`,
      detail: loserIsMe
        ? 'Last one holding cards. Shake it off and call a rematch.'
        : 'Everyone else escaped.',
    };
  }

  if (last.thoka && inPlayCards.length) {
    const taker = nameOf(players, last.userCallBazzi, userID);
    return {
      ...base,
      tone: 'pickup',
      title: 'Thoka!',
      detail:
        taker === 'You'
          ? 'Someone broke suit, so the pile comes to you. You had the highest card.'
          : `Someone broke suit, so ${taker} picks up the pile.`,
    };
  }

  if (
    !userDeck.length &&
    room.nextTurn === userID &&
    (players || []).filter(p => p.cardsLeft > 0).length > 1
  ) {
    return {
      ...base,
      tone: 'your-turn',
      title: 'Your turn: ask for cards',
      detail:
        'You won the last round with an empty hand. Ask the next player for cards to lead again.',
    };
  }

  if (!userDeck.length) {
    return {
      ...base,
      tone: 'escaped',
      title: 'You escaped!',
      detail: 'Your hand is empty. Watch who ends up as the Bhabi.',
    };
  }

  const alreadyPlayed = inPlayCards.some(c => c.userID === userID);

  if (room.nextTurn === userID && !alreadyPlayed) {
    if (!leadSuit) {
      const holdsAceOfSpades = userDeck.some(
        c => c.suite === 'spades' && c.value.trim() === 'A'
      );
      const totalCards = (players || []).reduce((s, p) => s + p.cardsLeft, 0);
      // Nobody has played or picked up yet: the full deck is still in hands.
      const opening = holdsAceOfSpades && totalCards >= 52;
      return {
        ...base,
        tone: 'your-turn',
        title: 'Your turn: lead',
        detail: opening
          ? 'You hold the Ace of spades, so you open the game.'
          : 'Play any card to start the round. The others must follow its suit.',
      };
    }

    const hasSuit = userDeck.some(c => c.suite === leadSuit);
    if (hasSuit) {
      return {
        ...base,
        mustFollow: true,
        tone: 'your-turn',
        title: `Your turn: follow ${SUIT_GLYPH[leadSuit]} ${SUIT_NAME[leadSuit]}`,
        detail: `You have ${SUIT_NAME[leadSuit]}, so you must play one. Highlighted cards are playable.`,
      };
    }

    return {
      ...base,
      tone: 'your-turn',
      title: `Your turn: no ${SUIT_NAME[leadSuit]}`,
      detail: `Any card is allowed. Breaking suit sends the whole pile to whoever played the highest ${SUIT_NAME[leadSuit]}.`,
    };
  }

  const who = nameOf(players, room.nextTurn, userID);
  return {
    ...base,
    tone: 'their-turn',
    title: who === 'You' ? 'Waiting for the round to finish' : `${who}'s turn`,
    detail: leadSuit
      ? `Round is following ${SUIT_GLYPH[leadSuit]} ${SUIT_NAME[leadSuit]}.`
      : 'A new round is about to start.',
  };
};
